import React, {useEffect, useRef, useState} from "react";
import NavBar from "../UserNavBar2";
import CourseNavbar from "../landing/CourseNavbar";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

/**
 * Shared chrome for learner sub-pages: the learner navbar plus a consistent,
 * structured page header.
 *
 *   <LearnerShell title="Books" subtitle="…" icon={faBook}>…</LearnerShell>
 *
 * The navbar search box is always controlled: pass `onSearchChange` to react to
 * it (debounced 350ms), otherwise it simply holds what the learner typed.
 */
const LearnerShell = ({title, subtitle, icon, actions, search, setSearch, onSearchChange, courseNavbar = false, children}) => {
  const [localSearch, setLocalSearch] = useState(search || "");
  const value = search !== undefined ? search : localSearch;

  // Keep the latest callback without re-arming the debounce on every render.
  const handlerRef = useRef(onSearchChange);
  useEffect(() => {
    handlerRef.current = onSearchChange;
  }, [onSearchChange]);

  const primed = useRef(false);
  useEffect(() => {
    const term = String(value || "").trim();
    if (!handlerRef.current) return undefined;
    // Skip the mount pass so pages do not re-request on first paint.
    if (!primed.current) {
      primed.current = true;
      return undefined;
    }
    const timer = setTimeout(() => handlerRef.current?.(term), 350);
    return () => clearTimeout(timer);
  }, [value]);

  const handleSearchChange = (next) => {
    if (search === undefined) setLocalSearch(next);
    setSearch?.(next);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {courseNavbar ? <CourseNavbar /> : <NavBar search={value} setSearch={handleSearchChange} />}
      <main className="mx-auto w-full max-w-7xl px-4 pb-24 pt-6">
        {title || subtitle || actions ? (
          <header className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="m-0 flex items-center gap-2 text-xl font-bold text-slate-900 sm:text-2xl">
                {icon ? <FontAwesomeIcon icon={icon} className="text-blue-900" /> : null}
                <span className="truncate">{title}</span>
              </h1>
              {subtitle ? <p className="m-0 mt-1 text-sm text-slate-500">{subtitle}</p> : null}
            </div>
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
          </header>
        ) : null}
        {children}
      </main>
    </div>
  );
};

export default LearnerShell;

